import type { ContactWithFieldValues, SearchContactsRequest } from "../../../../../api/types/fundStructureTypes";
import { ContactStatus } from "../../../../../api/types/fundStructureTypes";
import { contactStatusCaptionsMap } from "../../../../../utilities/enumCaptions";
import type { FilterState } from "../../../../common/filters/filterState";
import type {
  SearchFilterDefinition,
  SelectOption,
  TableFilter,
  TableFilterDefinition,
} from "../../../../common/filters/filterTypes";

const statusOptions: SelectOption[] = [ContactStatus.NotRegistered, ContactStatus.Registered].map((status) => ({
  value: String(status),
  label: contactStatusCaptionsMap.get(status) ?? "",
}));

const commonDefinitions: TableFilterDefinition<ContactWithFieldValues>[] = [
  {
    id: "investor",
    name: "Investor",
    type: "multi_select",
    operatorOptions: ["one_of"],
    asyncOptions: true,
  },
  {
    id: "fund",
    name: "Fund",
    type: "multi_select",
    operatorOptions: ["one_of"],
    asyncOptions: true,
  },
  {
    id: "totalLiveMessages",
    name: "Live Documents",
    type: "number",
    operatorOptions: ["between", "greater_than", "less_than"],
  },
];

export const filterDefinitionsForAllContacts: TableFilterDefinition<ContactWithFieldValues>[] = [
  ...commonDefinitions,
  {
    id: "hasAccessToInvestorPortal",
    name: "Access to Portal",
    type: "boolean",
  },
  {
    id: "status",
    name: "Registration",
    type: "multi_select",
    operatorOptions: ["one_of"],
    predefinedOptions: statusOptions,
  },
  {
    id: "invitationSentAt",
    name: "Invited to Portal",
    type: "date",
    operatorOptions: ["range"],
  },
  {
    id: "lastActivity",
    name: "Last Activity",
    type: "date",
    operatorOptions: ["range"],
  },
];

export const filterDefinitionsForNotInvitedContacts: TableFilterDefinition<ContactWithFieldValues>[] = [
  ...commonDefinitions,
];

export const searchDefinition: SearchFilterDefinition<ContactWithFieldValues> = {
  getFieldValues: (row) => [
    row.email,
    row.name,
    contactStatusCaptionsMap.get(row.status) ?? "",
    ...row.fundInvestors.map((fi) => fi.investorName),
    ...row.fundInvestors.map((fi) => fi.fundName),
    row.id,
  ],
};

type ContactFilter = TableFilter<ContactWithFieldValues>;

const getMultiSelectValues = (filterId: string, filters: ContactFilter[]): string[] | undefined => {
  const filter = filters.find((f) => f.id === filterId);
  if (filter?.type !== "multi_select" || filter.value.operator !== "one_of") {
    return undefined;
  }
  const selectedValues = filter.value.selectedValues;
  return selectedValues?.length ? selectedValues : undefined;
};

const getBooleanValue = (filterId: string, filters: ContactFilter[]): boolean | undefined => {
  const filter = filters.find((f) => f.id === filterId);
  if (filter?.type !== "boolean" || filter.value.operator !== "equals") {
    return undefined;
  }
  return filter.value.operatorValue;
};

const getDateRange = (filterId: string, filters: ContactFilter[]): [string | undefined, string | undefined] => {
  const filter = filters.find((f) => f.id === filterId);
  if (filter?.type !== "date" || filter.value.operator !== "range") {
    return [undefined, undefined];
  }
  const [from, to] = filter.value.range ?? [];
  if (!from || !to) {
    return [undefined, undefined];
  }
  return [from, to];
};

const getNumberRange = (filterId: string, filters: ContactFilter[]): [number | undefined, number | undefined] => {
  const filter = filters.find((f) => f.id === filterId);
  if (filter?.type !== "number") {
    return [undefined, undefined];
  }
  const { operator, number, secondNumber } = filter.value;
  switch (operator) {
    case "between":
      return number !== undefined && secondNumber !== undefined ? [number, secondNumber] : [undefined, undefined];
    case "greater_than":
      return number !== undefined ? [number + 1, undefined] : [undefined, undefined];
    case "less_than":
      return number !== undefined ? [undefined, number - 1] : [undefined, undefined];
    default:
      return [undefined, undefined];
  }
};

export const getContactsFilterPayload = (filterState: FilterState<ContactWithFieldValues>): SearchContactsRequest => {
  const { visibleFilters } = filterState;

  const statuses = getMultiSelectValues("status", visibleFilters)?.map(Number);
  const [invitationSentFrom, invitationSentTo] = getDateRange("invitationSentAt", visibleFilters);
  const [lastActivityFrom, lastActivityTo] = getDateRange("lastActivity", visibleFilters);
  const [liveDocumentsFrom, liveDocumentsTo] = getNumberRange("totalLiveMessages", visibleFilters);

  return {
    fieldIds: [],
    includeFundInvestors: true,
    includeInboxData: true,
    searchTerm: filterState.search.value || undefined,
    investorIds: getMultiSelectValues("investor", visibleFilters),
    fundIds: getMultiSelectValues("fund", visibleFilters),
    statuses,
    hasAccessToInvestorPortal: getBooleanValue("hasAccessToInvestorPortal", visibleFilters),
    invitationSentFrom,
    invitationSentTo,
    lastActivityFrom,
    lastActivityTo,
    liveDocumentsFrom,
    liveDocumentsTo,
  };
};
